import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const governmentUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Political Systems, Regimes, and Governments",
  examWeight: "15–20%",
  focus: "Understanding how political scientists analyze data, define political organizations, and classify regimes based on power distribution and citizen participation.",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: The Practice of Political Scientists",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Empirical vs. Normative Statements</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📈 Empirical Statements</h4>
                <p className="text-sm text-slate-700 mt-2">Fact-based, objective statements describing "what is." Rely on quantitative or qualitative data.</p>
                <p className="text-xs text-blue-600 mt-2 italic">Example: "Russia's GDP per capita is lower than the UK's."</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">💭 Normative Statements</h4>
                <p className="text-sm text-slate-700 mt-2">Value-based, subjective statements describing "what ought to be." Rely on value judgments.</p>
                <p className="text-xs text-purple-600 mt-2 italic">Example: "Russia should adopt a more democratic electoral system."</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔬 Types of Data Analysis</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <KeyTerm 
                term="Quantitative Data" 
                definition="Numerical data used to make comparisons (statistics, percentages, graphs)."
              />
              <KeyTerm 
                term="Qualitative Data" 
                definition="Descriptive data used to understand depth and context (interviews, speeches, documents)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔗 Correlation vs. Causation</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">📊 Correlation</h4>
                <p className="text-sm text-slate-700 mt-2">An association between two variables. Does NOT prove causation.</p>
                <p className="text-xs text-green-600 mt-2">Example: As GDP rises, literacy rates often rise.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">⚡ Causation</h4>
                <p className="text-sm text-slate-700 mt-2">A direct cause-and-effect relationship. Difficult to prove in comparative politics.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📋 Key Data Indicators (Must Know)</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="HDI (Human Development Index)" 
                definition="Measures health (life expectancy), knowledge (schooling), and standard of living (GNI per capita)."
              />
              <KeyTerm 
                term="GDP (Gross Domestic Product)" 
                definition="Total value of goods and services produced within a country in a year."
              />
              <KeyTerm 
                term="GDP per Capita" 
                definition="GDP divided by population; better indicator of average standard of living."
              />
              <KeyTerm 
                term="Gini Index" 
                definition="Measures income inequality. 0 = perfect equality; 100 = perfect inequality."
              />
              <KeyTerm 
                term="Freedom House" 
                definition="Ranks countries on political rights and civil liberties (Free, Partly Free, Not Free)."
              />
              <KeyTerm 
                term="Transparency International" 
                definition="Measures perceived public sector corruption. Higher scores = less corruption."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Defining Political Organizations",
      content: (
        <div className="space-y-6">
          <ImportantNote>
            Understanding the distinction between state, regime, government, and nation is crucial for the AP exam!
          </ImportantNote>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ The State</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">A political institution with a monopoly on the legitimate use of force over a defined territory.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Key Characteristics:</strong> Permanent population, defined territory, governing institutions, sovereignty, international recognition.</p>
                <p className="text-sm text-slate-700"><strong>Sovereignty:</strong> The legal right and ability of a state to carry out actions independently of external actors.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ The Regime</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <h4 className="font-bold text-purple-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">The fundamental rules and norms of politics. Determines where power resides and how it is used.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Durability:</strong> Long-lasting; typically endure from government to government.</p>
                <p className="text-sm text-slate-700"><strong>Change:</strong> Usually only change through revolutions or coups.</p>
                <p className="text-xs text-purple-600 mt-2 italic">Example: Iran's 1979 transition from monarchy to Islamic Republic was a regime change.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👥 The Government</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <h4 className="font-bold text-green-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">The leadership or elite in charge of running the state.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Durability:</strong> Short-term; change frequently through elections, term limits, or death.</p>
                <p className="text-xs text-green-600 mt-2 italic">Example: UK Labour replacing Conservatives is a government change, not regime change.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 The Nation</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-4">
              <h4 className="font-bold text-orange-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">A group of people bound together by common political aspirations (self-government, sovereignty).</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Basis:</strong> Often based on common race, language, religion, or history.</p>
                <p className="text-sm text-slate-700"><strong>Multinational States:</strong> Nigeria and UK contain multiple nations within one state.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Democracy vs. Authoritarianism",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✅ Democratic Regimes</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <h4 className="font-bold text-green-800">Core Principle</h4>
              <p className="text-sm text-slate-700 mt-2">Power is derived from the people.</p>
            </div>
            <div className="space-y-3">
              <KeyTerm 
                term="Free and Fair Elections" 
                definition="Competition is real, and the outcome is uncertain."
              />
              <KeyTerm 
                term="Rule of Law" 
                definition="Everyone, including the government, is subject to the law."
              />
              <KeyTerm 
                term="Independent Judiciary" 
                definition="Courts can check the power of executive/legislative branches."
              />
              <KeyTerm 
                term="Civil Society" 
                definition="Robust and active groups independent of the state."
              />
              <KeyTerm 
                term="Transparency" 
                definition="Government operates openly."
              />
            </div>
            <p className="text-sm text-slate-700 mt-4"><strong>Course Examples:</strong> 🇬🇧 United Kingdom (Consolidated Democracy); 🇲🇽 Mexico (Developing Democracy)</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">❌ Authoritarian Regimes</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
              <h4 className="font-bold text-red-800">Core Principle</h4>
              <p className="text-sm text-slate-700 mt-2">A small group exercises power without being constitutionally responsible to the public.</p>
            </div>
            <div className="space-y-3">
              <KeyTerm 
                term="Rule BY Law" 
                definition="The state uses law as a tool to control citizens; the state is above the law."
              />
              <KeyTerm 
                term="Controlled Media" 
                definition="State censorship or ownership of media."
              />
              <KeyTerm 
                term="Restricted Participation" 
                definition="Elections may exist but are not competitive (e.g., vetting candidates in Iran)."
              />
            </div>
            <p className="text-sm text-slate-700 mt-4"><strong>Course Example:</strong> 🇨🇳 China (Authoritarian/One-Party State)</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Hybrid Regimes / Illiberal Democracies</h3>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-bold text-yellow-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">Possess democratic mechanisms (elections) but have weak/controlled institutions that limit competition and civil liberties.</p>
              <p className="text-sm text-slate-700 mt-3"><strong>Course Examples:</strong> 🇷🇺 Russia (under Putin); 🇳🇬 Nigeria (struggling with consolidation)</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Democratization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 The Process of Democratization</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Transition</h4>
                <p className="text-sm text-slate-700 mt-2">The toppling of an authoritarian regime and adoption of democratic institutions.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Consolidation</h4>
                <p className="text-sm text-slate-700 mt-2">The process by which a new democratic order becomes institutionalized and "the only game in town."</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✨ Signs of Democratization</h3>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Electoral Competition</h4>
                <p className="text-sm text-slate-700 mt-2">Moving from one-party rule to multiparty competition.</p>
                <p className="text-xs text-purple-600 mt-2">🇲🇽 Mexico: End of PRI dominance in 2000 signaled democratization.</p>
                <p className="text-xs text-purple-600">🇳🇬 Nigeria: 2015 election marked first peaceful transfer from incumbent to opposition.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Citizen Participation</h4>
                <p className="text-sm text-slate-700 mt-2">Expansion of suffrage and protection of civil rights.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Transparency</h4>
                <p className="text-sm text-slate-700 mt-2">Openness in government decision-making reduces corruption.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚧 Obstacles to Democratization</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Corruption" 
                definition="Undermines trust in institutions (major issue in Nigeria, Mexico, Russia)."
              />
              <KeyTerm 
                term="Election Fraud" 
                definition="Ballot stuffing, intimidation, disqualifying opposition (Russia, Iran)."
              />
              <KeyTerm 
                term="Violence" 
                definition="Political violence intimidates voters and destabilizes transitions."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Sources of Power and Authority",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💪 Power vs. Authority</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Power</h4>
                <p className="text-sm text-slate-700 mt-2">The ability to direct the behavior of others through coercion, persuasion, or leadership.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Authority</h4>
                <p className="text-sm text-slate-700 mt-2">The legal right to exercise power.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Comparative Sources by Country</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Source:</strong> The Communist Party of China (CCP)</p>
                <p className="text-sm text-slate-700"><strong>Mechanism:</strong> Party is supreme; power from political elites and military (PLA)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Source:</strong> Theocracy (God) and the People (Elections)</p>
                <p className="text-sm text-slate-700"><strong>Mechanism:</strong> Velayat-e Faqih (Guardianship of the Jurist); Supreme Leader holds ultimate power</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇲🇽 Mexico & 🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Source:</strong> Constitutions</p>
                <p className="text-sm text-slate-700"><strong>Mechanism:</strong> Transition to multiparty constitutional republics; legitimacy from elections</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Source:</strong> Constitution (formally), but practically a Personality Cult</p>
                <p className="text-sm text-slate-700"><strong>Mechanism:</strong> Power highly concentrated in President (Putin); "Managed Democracy"</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Source:</strong> Tradition and Rational-Legal Authority</p>
                <p className="text-sm text-slate-700"><strong>Mechanism:</strong> No single written constitution; power from Acts of Parliament and common law</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Change in Power and Authority",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔥 Regime Change (Fundamental)</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Revolution</h4>
                <p className="text-sm text-slate-700 mt-2">A public seizure of the state to overturn the existing government and regime.</p>
                <p className="text-xs text-red-600 mt-2">Examples: 🇮🇷 Iran (1979) Islamic Revolution; 🇨🇳 China (1949) Communist Revolution</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Coup d'état</h4>
                <p className="text-sm text-slate-700 mt-2">A sudden, violent, and illegal seizure of power from a government, usually by the military.</p>
                <p className="text-xs text-orange-600 mt-2">Example: 🇳🇬 Nigeria experienced multiple military coups between 1966 and 1999</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗳️ Government Change (Regular)</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Elections</h4>
                <p className="text-sm text-slate-700 mt-2">Peaceful transfer of power through democratic voting.</p>
                <p className="text-xs text-green-600 mt-2">Examples: 🇬🇧 UK General Elections; 🇲🇽 Mexico Presidential Elections (Sexenio)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Appointments</h4>
                <p className="text-sm text-slate-700 mt-2">In authoritarian systems, change often happens through opaque internal party mechanisms.</p>
                <p className="text-xs text-blue-600 mt-2">Example: 🇨🇳 China leadership transition (Xi Jinping removing term limits)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-7",
      title: "Topic 1.7: Federal and Unitary Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ Federal Systems</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">Significant powers are devolved to regional bodies (states, provinces) that are constitutionally protected and cannot be easily taken away by the center.</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🇲🇽 Mexico:</strong> Federal to accommodate diverse geography and regional interests</p>
              <p className="text-sm text-slate-700"><strong>🇳🇬 Nigeria:</strong> Federal to manage severe ethnic and religious diversity</p>
              <p className="text-sm text-slate-700"><strong>🇷🇺 Russia:</strong> Asymmetric Federalism; power has become highly centralized under Putin</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎯 Unitary Systems</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <h4 className="font-bold text-purple-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">Most political power is concentrated at the national level. Local authority is granted by the central government and can be retracted.</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🇨🇳 China:</strong> Highly centralized unitary state to maintain CCP control</p>
              <p className="text-sm text-slate-700"><strong>🇮🇷 Iran:</strong> Unitary state to ensure religious consistency and control</p>
              <p className="text-sm text-slate-700"><strong>🇬🇧 United Kingdom:</strong> Unitary state, but utilizes Devolution (granting power to Scotland, Wales, N. Ireland). Central parliament can theoretically revoke these powers.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-8",
      title: "Topic 1.8: Political Legitimacy",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👑 What is Legitimacy?</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
              <p className="text-sm text-slate-700"><strong>Legitimacy:</strong> The belief by the citizenry that the government has the right to rule. Without it, states must rely on coercion (force).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎭 Types of Legitimacy (Max Weber)</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Traditional Legitimacy</h4>
                <p className="text-sm text-slate-700 mt-2">"It has always been this way."</p>
                <p className="text-xs text-green-600 mt-2">Examples: British Monarchy; Theocracy in Iran</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Charismatic Legitimacy</h4>
                <p className="text-sm text-slate-700 mt-2">Based on the dynamic personality of a leader.</p>
                <p className="text-xs text-orange-600 mt-2">Examples: Mao Zedong, Ayatollah Khomeini, Putin</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Rational-Legal Legitimacy</h4>
                <p className="text-sm text-slate-700 mt-2">Based on a system of laws and procedures that are highly institutionalized.</p>
                <p className="text-xs text-blue-600 mt-2">Examples: UK Parliament, Mexico's Constitution</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Sources of Legitimacy by Country</h3>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🇨🇳 China:</strong> Performance Legitimacy (Economic growth), Nationalism</p>
              <p className="text-sm text-slate-700"><strong>🇷🇺 Russia:</strong> Strong leadership (Restoring Russia's greatness), Nationalism</p>
              <p className="text-sm text-slate-700"><strong>🇮🇷 Iran:</strong> Religious texts (Quran/Sharia), Revolutionary credentials</p>
              <p className="text-sm text-slate-700"><strong>🇬🇧 UK/🇲🇽 Mexico/🇳🇬 Nigeria:</strong> Rational-Legal (Elections, Constitutions)</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-9",
      title: "Topic 1.9: Sustaining Legitimacy",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✅ Reinforcing Legitimacy</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">💰 Economic Well-being</h4>
                <p className="text-sm text-slate-700 mt-2">If people are wealthy, they tend to accept the government (China's social contract).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🎌 Tradition/Nationalism</h4>
                <p className="text-sm text-slate-700 mt-2">Using symbols and history to rally support (Russia, Iran).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🗳️ Fair Elections</h4>
                <p className="text-sm text-slate-700 mt-2">Gives citizens a stake in the system (UK, Mexico).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">⚙️ Effective Policy</h4>
                <p className="text-sm text-slate-700 mt-2">Solving problems (security, infrastructure).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">❌ Undermining Legitimacy</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Corruption" 
                definition="When leaders use state resources for personal gain (Nigeria, Russia, Mexico)."
              />
              <KeyTerm 
                term="Ineffective Rule of Law" 
                definition="Arbitrary arrests or lack of justice."
              />
              <KeyTerm 
                term="Economic Crisis" 
                definition="High inflation or unemployment."
              />
              <KeyTerm 
                term="Election Fraud" 
                definition="'Rigged' elections destroy trust (Iran 2009 Green Movement; Russia)."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-10",
      title: "Topic 1.10: Political Stability",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ What is Political Stability?</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-sm text-slate-700"><strong>Stability:</strong> The ability of a state to fulfill its basic functions and resist collapse.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Challenges to Stability</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Loss of Sovereignty</h4>
                <p className="text-sm text-slate-700 mt-2">External control or internal rivals challenging the state's monopoly on force.</p>
                <p className="text-xs text-red-600 mt-2">Examples: Drug Cartels in Mexico; Boko Haram in Nigeria</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Protest and Unrest</h4>
                <p className="text-sm text-slate-700 mt-2">Citizens demanding change.</p>
                <p className="text-xs text-orange-600 mt-2">Examples: Navalny protests in Russia; 1989 Tiananmen Square in China</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Separatism</h4>
                <p className="text-sm text-slate-700 mt-2">Regions wanting to break away.</p>
                <p className="text-xs text-purple-600 mt-2">Examples: Scotland in UK; Chechnya in Russia; Uighurs/Tibetans in China</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔧 State Responses to Instability</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">⚔️ Coercion</h4>
                <p className="text-sm text-slate-700 mt-2">Using force, military, or secret police to crush dissent (China, Iran, Russia).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🤝 Co-optation</h4>
                <p className="text-sm text-slate-700 mt-2">Buying off critics or giving them a place in the system to silence them (Corporatism).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">📋 Reform</h4>
                <p className="text-sm text-slate-700 mt-2">Changing methods or policies to satisfy demands (Mexico creating independent electoral commission).</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default governmentUnit1Notes;
